// ===============================================================
// V·IANet Requests (M16) — TiposTab: CRUD de TipoSolicitud
// ===============================================================

import { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { request } from '../../../shared/services/api';
import { useRequestsConfig } from '../hooks/useRequestsConfig';
import type { TipoSolicitud, GrupoSolicitud, SimpleResponse } from '../types/requests.types';
import { C } from '../../../../lib/design';

interface ProcesoOption {
  id: string;
  codigo: string;
  nombre: string;
}

interface TipoForm {
  codigo: string;
  nombre: string;
  descripcion: string;
  grupoId: string;
  procesoId: string;
  requiereFirma: boolean;
  nivelFirma: string;
}

const EMPTY_FORM: TipoForm = {
  codigo: '',
  nombre: '',
  descripcion: '',
  grupoId: '',
  procesoId: '',
  requiereFirma: false,
  nivelFirma: '',
};

const NIVELES_FIRMA = ['BASICA', 'AVANZADA', 'CUALIFICADA'];

export function TiposTab() {
  const { t } = useI18n();
  const [grupoFilter, setGrupoFilter] = useState('');

  const queryParams = useMemo(
    () => (grupoFilter ? { grupoId: grupoFilter } : undefined),
    [grupoFilter],
  );

  const { items, loading, meta, page, setPage, crear, actualizar, eliminar } =
    useRequestsConfig<TipoSolicitud>('/requests/tipos', { paginated: true, queryParams });

  const [grupos, setGrupos] = useState<GrupoSolicitud[]>([]);
  const [procesos, setProcesos] = useState<ProcesoOption[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<TipoForm>(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Fetch grupos & procesos for selects
  useEffect(() => {
    const fetchAux = async () => {
      try {
        const [gRes, pRes] = await Promise.all([
          request<SimpleResponse<GrupoSolicitud>>('/requests/grupos'),
          request<SimpleResponse<ProcesoOption>>('/procesos'),
        ]);
        setGrupos(gRes.data);
        setProcesos(pRes.data);
      } catch {
        // silent — selects will be empty
      }
    };
    fetchAux();
  }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (tipo: TipoSolicitud) => {
    setEditId(tipo.id);
    setForm({
      codigo: tipo.codigo,
      nombre: tipo.nombre,
      descripcion: tipo.descripcion ?? '',
      grupoId: tipo.grupoId,
      procesoId: tipo.procesoId ?? '',
      requiereFirma: tipo.requiereFirma,
      nivelFirma: tipo.nivelFirma ?? '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        procesoId: form.procesoId || null,
        nivelFirma: form.requiereFirma && form.nivelFirma ? form.nivelFirma : null,
      };
      if (editId) {
        await actualizar(editId, payload as unknown as Partial<TipoSolicitud>);
      } else {
        await crear(payload as unknown as Partial<TipoSolicitud>);
      }
      closeModal();
    } catch {
      // error handled by hook
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await eliminar(confirmDeleteId);
    } catch {
      // error handled by hook
    }
    setConfirmDeleteId(null);
  };

  if (loading) {
    return <LoadingSpinner size="sm" muted />;
  }

  const canSave = form.codigo.trim() && form.nombre.trim() && form.grupoId;

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
          {t.requests?.tipos ?? 'Tipos de solicitud'}
        </h3>
        <button
          onClick={openCreate}
          style={{
            padding: '8px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#1F3864',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t.requests?.nuevo ?? 'Nuevo tipo'}
        </button>
      </div>

      {/* Filter by grupo */}
      <div style={{ marginBottom: 16 }}>
        <select
          value={grupoFilter}
          onChange={(e) => {
            setGrupoFilter(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid #D1D5DB',
            fontSize: 13,
            minWidth: 220,
            color: C.gray700,
          }}
        >
          <option value="">{t.requests?.todosGrupos ?? 'Todos los grupos'}</option>
          {grupos.map((g) => (
            <option key={g.id} value={g.id}>
              {g.nombre}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.gray400 }}>
          {t.requests?.sinDatos ?? 'No hay tipos configurados'}
        </div>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.gray500,
                  }}
                >
                  Codigo
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.gray500,
                  }}
                >
                  Nombre
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.gray500,
                  }}
                >
                  Grupo
                </th>
                <th
                  style={{
                    textAlign: 'center',
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.gray500,
                  }}
                >
                  Firma
                </th>
                <th
                  style={{
                    textAlign: 'center',
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.gray500,
                  }}
                >
                  Activo
                </th>
                <th
                  style={{
                    textAlign: 'right',
                    padding: '12px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.gray500,
                  }}
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((tipo) => (
                <tr key={tipo.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: 14,
                      fontWeight: 500,
                      fontFamily: 'monospace',
                    }}
                  >
                    {tipo.codigo}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>{tipo.nombre}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: C.gray500 }}>
                    {tipo.grupo?.nombre ?? '-'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, textAlign: 'center' }}>
                    {tipo.requiereFirma ? (
                      <span
                        style={{
                          background: '#EEF2FF',
                          color: '#4338CA',
                          padding: '2px 8px',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        {tipo.nivelFirma ?? 'Si'}
                      </span>
                    ) : (
                      <span style={{ color: C.gray400 }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        display: 'inline-block',
                        background: tipo.activo ? '#10B981' : '#D1D5DB',
                      }}
                    />
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <button
                      onClick={() => openEdit(tipo)}
                      title={t.requests?.editar ?? 'Editar'}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 4,
                        marginRight: 4,
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z"
                          stroke="#2F5496"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(tipo.id)}
                      title={t.requests?.eliminar ?? 'Eliminar'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M3 4h10M5.5 4V3a1 1 0 011-1h3a1 1 0 011 1v1M6 7v5M10 7v5M4 4l.7 9a1 1 0 001 .9h4.6a1 1 0 001-.9L12 4"
                          stroke="#DC2626"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {meta && meta.pages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 8,
                marginTop: 16,
              }}
            >
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                  color: page <= 1 ? '#D1D5DB' : C.gray700,
                  fontSize: 13,
                }}
              >
                Anterior
              </button>
              <span style={{ fontSize: 13, color: C.gray500 }}>
                {page} / {meta.pages}
              </span>
              <button
                onClick={() => setPage(Math.min(meta.pages, page + 1))}
                disabled={page >= meta.pages}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: page >= meta.pages ? 'not-allowed' : 'pointer',
                  color: page >= meta.pages ? '#D1D5DB' : C.gray700,
                  fontSize: 13,
                }}
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              width: 520,
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <h4 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {editId ? (t.requests?.editar ?? 'Editar tipo') : (t.requests?.nuevo ?? 'Nuevo tipo')}
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                  Codigo *
                  <input
                    value={form.codigo}
                    onChange={(e) => setForm((f) => ({ ...f, codigo: e.target.value }))}
                    placeholder="SOL-001"
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #D1D5DB',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  />
                </label>
                <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                  Nombre *
                  <input
                    value={form.nombre}
                    onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #D1D5DB',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  />
                </label>
              </div>

              <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                Descripcion
                <textarea
                  value={form.descripcion}
                  onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                  rows={2}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </label>

              <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                Grupo *
                <select
                  value={form.grupoId}
                  onChange={(e) => setForm((f) => ({ ...f, grupoId: e.target.value }))}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="">Seleccionar grupo...</option>
                  {grupos.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.nombre}
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                Proceso asociado
                <select
                  value={form.procesoId}
                  onChange={(e) => setForm((f) => ({ ...f, procesoId: e.target.value }))}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #D1D5DB',
                    fontSize: 14,
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="">Sin proceso</option>
                  {procesos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.codigo} - {p.nombre}
                    </option>
                  ))}
                </select>
              </label>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: C.gray700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.requiereFirma}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        requiereFirma: e.target.checked,
                        nivelFirma: e.target.checked ? f.nivelFirma : '',
                      }))
                    }
                    style={{ width: 16, height: 16, accentColor: '#1F3864' }}
                  />
                  Requiere firma
                </label>
              </div>

              {form.requiereFirma && (
                <label style={{ fontSize: 13, fontWeight: 500, color: C.gray700 }}>
                  Nivel de firma
                  <select
                    value={form.nivelFirma}
                    onChange={(e) => setForm((f) => ({ ...f, nivelFirma: e.target.value }))}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #D1D5DB',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">Seleccionar nivel...</option>
                    {NIVELES_FIRMA.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={closeModal}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={handleSave}
                disabled={!canSave}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: canSave ? '#1F3864' : C.gray400,
                  color: '#fff',
                  cursor: canSave ? 'pointer' : 'not-allowed',
                }}
              >
                {t.common.guardar}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 400 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#1F3864' }}>
              {t.requests?.confirmarEliminar ?? 'Confirmar eliminacion'}
            </h4>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: C.gray500 }}>
              {t.requests?.confirmarEliminarMsg ??
                'Esta accion no se puede deshacer. Se eliminara el tipo y sus configuraciones asociadas.'}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteId(null)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#DC2626',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.eliminar ?? 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
